<template>
  <!-- Loading state -->
  <div v-if="loading" class="flex items-center justify-center min-h-[400px]">
    <div class="flex flex-col items-center gap-3">
      <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span class="text-sm text-gray-500 dark:text-gray-400">Loading onboarding...</span>
    </div>
  </div>

  <!-- Error state -->
  <div v-else-if="error" class="flex items-center justify-center min-h-[400px]">
    <div class="text-center">
      <p class="text-red-600 dark:text-red-400 mb-3">{{ error }}</p>
      <button
        class="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
        @click="loadOnboarding"
      >
        Retry
      </button>
    </div>
  </div>

  <!-- Wizard layout -->
  <div v-else-if="onboarding" class="flex flex-col lg:flex-row gap-6 py-6 px-4 lg:px-8">
    <!-- Left sidebar -->
    <div class="lg:w-64 flex-shrink-0">
      <ProgressSidebar
        :steps="steps"
        :progress="onboarding.progress || {}"
        :current-step-index="currentStepIndex"
        :completion-percentage="onboarding.completion_percentage || 0"
        :business-name="onboarding.business_name || ''"
        :business-logo-url="onboarding.business_logo_url"
        :mode="mode"
        @navigate="goToStep"
      />
    </div>

    <!-- Right content area -->
    <div class="flex-1 min-w-0">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-semibold font-poppins text-gray-900 dark:text-gray-100">
            Onboarding
          </h2>
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="statusBadgeClasses"
          >
            {{ statusLabel }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <button
            v-if="mode === 'internal' && onboarding.status !== 'completed'"
            class="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            :disabled="sending"
            @click="handleSendToClient"
          >
            {{ sending ? 'Sending...' : 'Send to Client' }}
          </button>

          <button
            v-if="mode === 'internal' && onboarding.status === 'completed'"
            class="px-4 py-2 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
            :disabled="firingWebhooks"
            @click="handleFireWebhooks"
          >
            {{ firingWebhooks ? 'Sending...' : 'Fire Webhooks' }}
          </button>
        </div>
      </div>

      <!-- Webhook feedback -->
      <div v-if="webhookMessage" class="mb-4 p-3 rounded-lg text-sm bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
        {{ webhookMessage }}
      </div>

      <!-- Current step component -->
      <div class="mb-6">
        <component
          :is="currentStepComponent"
          ref="stepRef"
          :step-key="currentStep.key"
          :label="currentStep.label"
          :onboarding="onboarding"
          :mode="mode"
        />
      </div>

      <!-- Navigation buttons -->
      <div class="flex items-center justify-between pb-6">
        <button
          v-if="currentStepIndex > 0"
          class="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          :disabled="saving"
          @click="prevStep"
        >
          Back
        </button>
        <div v-else />

        <div class="flex items-center gap-3">
          <span v-if="saving" class="text-xs text-gray-400">Saving...</span>
          <button
            v-if="isLastStep"
            class="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
            :class="mode === 'client'
              ? 'bg-[#12e40b] hover:bg-[#10cc0a]'
              : 'bg-primary-500 hover:bg-primary-600'"
            :disabled="saving"
            @click="handleSubmit"
          >
            {{ saving ? 'Submitting...' : (mode === 'client' ? 'Submit' : 'Complete') }}
          </button>
          <button
            v-else
            class="px-5 py-2.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            :disabled="saving"
            @click="nextStep"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
  getOnboarding,
  updateOnboardingStep,
  sendOnboardingToClient,
  completeOnboarding,
  fireWebhooks as fireWebhooksApi,
  getOnboardingByToken,
  updateOnboardingStepByToken,
  submitOnboardingByToken
} from '~/javascript/api/onboardings'
import ProgressSidebar from '~/javascript/components/onboardings/progress_sidebar.vue'
import PlaceholderStep from '~/javascript/components/onboardings/steps/placeholder_step.vue'
import BasicsStep from '~/javascript/components/onboardings/steps/basics_step.vue'
import ServicesStep from '~/javascript/components/onboardings/steps/services_step.vue'
import IntegrationsStep from '~/javascript/components/onboardings/steps/integrations_step.vue'
import SeoAccessStep from '~/javascript/components/onboardings/steps/seo_access_step.vue'
import SeoHostingStep from '~/javascript/components/onboardings/steps/seo_hosting_step.vue'
import SeoLocationsStep from '~/javascript/components/onboardings/steps/seo_locations_step.vue'
import SeoMetricsStep from '~/javascript/components/onboardings/steps/seo_metrics_step.vue'
import PpcDetailsStep from '~/javascript/components/onboardings/steps/ppc_details_step.vue'
import InternalStep from '~/javascript/components/onboardings/steps/internal_step.vue'
import ReviewStep from '~/javascript/components/onboardings/steps/review_step.vue'

const stepComponentMap = {
  basics: BasicsStep,
  services: ServicesStep,
  integrations: IntegrationsStep,
  seo_access: SeoAccessStep,
  seo_hosting: SeoHostingStep,
  seo_locations: SeoLocationsStep,
  seo_metrics: SeoMetricsStep,
  ppc_details: PpcDetailsStep,
  internal: InternalStep,
  review: ReviewStep
}

const props = defineProps({
  mode: { type: String, required: true, validator: v => ['internal', 'client'].includes(v) },
  businessId: { type: String, default: null },
  token: { type: String, default: null }
})

const onboarding = ref(null)
const currentStepIndex = ref(0)
const loading = ref(true)
const saving = ref(false)
const sending = ref(false)
const firingWebhooks = ref(false)
const webhookMessage = ref('')
const error = ref(null)
const stepRef = ref(null)

// Step definitions derived from onboarding data
const steps = computed(() => {
  if (!onboarding.value?.step_definitions) return []
  return onboarding.value.step_definitions
})

const currentStep = computed(() => {
  return steps.value[currentStepIndex.value] || { key: '', label: '' }
})

const currentStepComponent = computed(() => {
  return stepComponentMap[currentStep.value.key] || PlaceholderStep
})

const isLastStep = computed(() => {
  return currentStepIndex.value === steps.value.length - 1
})

const statusLabel = computed(() => {
  const s = onboarding.value?.status
  const labels = { draft: 'Draft', sent: 'Sent', in_progress: 'In Progress', completed: 'Completed' }
  return labels[s] || s || 'Unknown'
})

const statusBadgeClasses = computed(() => {
  const s = onboarding.value?.status
  switch (s) {
    case 'draft':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    case 'sent':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
    case 'completed':
      return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
})

// Load onboarding data
async function loadOnboarding() {
  loading.value = true
  error.value = null

  try {
    let response
    if (props.mode === 'internal') {
      response = await getOnboarding(props.businessId)
    } else {
      response = await getOnboardingByToken(props.token)
    }
    onboarding.value = response.data

    // Restore last step position if available
    if (onboarding.value.current_step) {
      const idx = steps.value.findIndex(s => s.key === onboarding.value.current_step)
      if (idx >= 0) currentStepIndex.value = idx
    }
  } catch (e) {
    error.value = e.response?.data?.error || 'Failed to load onboarding data.'
  } finally {
    loading.value = false
  }
}

// Save current step data
async function saveCurrentStep() {
  if (!stepRef.value?.getData) return

  const data = stepRef.value.getData()
  if (!data || Object.keys(data).length === 0) return

  saving.value = true
  try {
    let response
    if (props.mode === 'internal') {
      response = await updateOnboardingStep(props.businessId, currentStep.value.key, data)
    } else {
      response = await updateOnboardingStepByToken(props.token, currentStep.value.key, data)
    }
    onboarding.value = response.data
  } catch (e) {
    console.error('Failed to save step:', e)
  } finally {
    saving.value = false
  }
}

// Navigation
async function goToStep(index) {
  if (index === currentStepIndex.value) return
  if (index < 0 || index >= steps.value.length) return

  await saveCurrentStep()
  currentStepIndex.value = index
}

async function nextStep() {
  if (isLastStep.value) return
  await saveCurrentStep()
  currentStepIndex.value++
}

async function prevStep() {
  if (currentStepIndex.value <= 0) return
  await saveCurrentStep()
  currentStepIndex.value--
}

// Submit / Complete
async function handleSubmit() {
  await saveCurrentStep()

  saving.value = true
  try {
    let response
    if (props.mode === 'internal') {
      response = await completeOnboarding(props.businessId)
    } else {
      response = await submitOnboardingByToken(props.token)
    }
    onboarding.value = response.data
  } catch (e) {
    console.error('Failed to submit:', e)
  } finally {
    saving.value = false
  }
}

// Send to client (internal only)
async function handleSendToClient() {
  sending.value = true
  try {
    const response = await sendOnboardingToClient(props.businessId)
    onboarding.value = response.data
  } catch (e) {
    console.error('Failed to send to client:', e)
  } finally {
    sending.value = false
  }
}

// Fire webhooks (internal only, completed onboarding)
async function handleFireWebhooks() {
  firingWebhooks.value = true
  try {
    const response = await fireWebhooksApi(props.businessId)
    webhookMessage.value = response.data.message || 'Webhooks fired successfully'
    setTimeout(() => { webhookMessage.value = '' }, 5000)
  } catch (e) {
    webhookMessage.value = e.response?.data?.message || 'Failed to fire webhooks'
    setTimeout(() => { webhookMessage.value = '' }, 5000)
  } finally {
    firingWebhooks.value = false
  }
}

// Initialize
loadOnboarding()
</script>
